/**
 * Generated exercises.
 *
 * Hand-authoring a thousand exercises is neither possible nor desirable: what
 * matters is that a student who gets `3/4 + 1/6` wrong immediately gets another
 * problem *of the same shape* with different numbers. So most exercises come
 * from templates — a template is a small function from a seeded RNG to an
 * `Exercise`, and it owns its own distractors and worked explanation.
 *
 * Generation is seeded (never `Math.random`) so a reloaded page shows the same
 * problem and a failing test can name the exact seed.
 */

import { createRng, type Rng } from '@yuan-math/math';
import { grade } from './grade.ts';
import type { Difficulty, Exercise, Trap } from './types.ts';

export type TemplateContext = {
  rng: Rng;
  difficulty: Difficulty;
};

/** Everything a template produces; identity and provenance are added by the engine. */
export type TemplateResult = Omit<Exercise, 'id' | 'conceptId' | 'difficulty' | 'source'>;

export type ExerciseTemplate = {
  id: string;
  conceptId: string;
  /** Difficulties this template can produce, ascending. */
  difficulties: Difficulty[];
  /** One-line description for the content dashboard. */
  description: string;
  build(context: TemplateContext): TemplateResult;
};

export function generateExercise(template: ExerciseTemplate, difficulty: Difficulty, seed: string): Exercise {
  const rng = createRng(`${template.id}:${difficulty}:${seed}`);
  const result = template.build({ rng, difficulty });
  const exercise: Exercise = {
    ...result,
    id: `${template.id}-${difficulty}-${seed}`,
    conceptId: template.conceptId,
    difficulty,
    source: { templateId: template.id, seed },
  };
  return applySafeTraps(exercise);
}

/**
 * Drops any trap that happens to coincide with the correct answer.
 *
 * Generated problems have parameter ranges, and a distractor formula that is
 * wrong for most parameters can be right for a few — a subtraction that needs
 * no regrouping, say. Telling a student who answered correctly that they made a
 * mistake is the worst failure this engine has, so the guard lives here rather
 * than relying on every template author to remember it.
 */
function applySafeTraps(exercise: Exercise): Exercise {
  if (!exercise.traps?.length) return exercise;
  const safe: Trap[] = exercise.traps.filter(
    (trap) => !grade(exercise, { kind: 'text', value: trap.response }).correct,
  );
  if (safe.length === exercise.traps.length) return exercise;
  const { traps: _dropped, ...rest } = exercise;
  return safe.length > 0 ? { ...rest, traps: safe } : rest;
}

/** A rough content-inventory number: how many distinct items a template spans. */
export function estimateVariants(template: ExerciseTemplate, samples = 60): number {
  const seen = new Set<string>();
  for (const difficulty of template.difficulties) {
    for (let i = 0; i < samples; i += 1) {
      const exercise = generateExercise(template, difficulty, `v${i}`);
      seen.add(fingerprint(exercise));
    }
  }
  return seen.size;
}

/** Two exercises are "the same problem" when the question renders identically. */
export function fingerprint(exercise: Exercise): string {
  const { prompt, latex, choices, figure } = exercise.question;
  return [prompt, latex ?? '', choices?.map((c) => c.latex ?? c.label).join('|') ?? '', figure ? JSON.stringify(figure) : '']
    .join('§');
}

export function clampDifficulty(value: number): Difficulty {
  return Math.min(5, Math.max(1, Math.round(value))) as Difficulty;
}
